import React, { useRef, useState, useCallback, DragEvent } from 'react';
import { Button, Typography, Alert } from 'antd';
import {
  InboxOutlined,
  UploadOutlined,
  PictureOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { FileUploadProps, CONSTANTS } from '../types';
import { FileUtils } from '../utils/fileUtils';
import './FileUpload.css';

// const { Dragger } = Upload;
const { Text, Title } = Typography;

interface FileUploadState {
  dragActive: boolean;
  uploading: boolean;
  error: string | null;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onFilesAdded,
  maxFileSize = CONSTANTS.MAX_FILE_SIZE,
  maxFiles = CONSTANTS.MAX_BATCH_SIZE,
  acceptedFormats = CONSTANTS.SUPPORTED_FORMATS,
  disabled = false
}) => {
  const [state, setState] = useState<FileUploadState>({
    dragActive: false,
    uploading: false,
    error: null
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // 处理文件添加
  const handleFilesAdded = useCallback(async (files: File[]) => {
    setState(prev => ({ ...prev, uploading: true, error: null }));

    try {
      // 验证文件
      const { validFiles, invalidFiles } = FileUtils.validateFileList(files);

      if (invalidFiles.length > 0) {
        const errorMessages = invalidFiles.map(({ file, error }) =>
          `${file.name}: ${error.message}`
        ).join('\n');

        setState(prev => ({
          ...prev,
          error: `部分文件验证失败:\n${errorMessages}`
        }));
      }

      // 为有效文件创建FileInfo
      if (validFiles.length > 0) {
        const fileInfos = await FileUtils.createFileInfoList(validFiles, true);
        onFilesAdded(fileInfos);
      }

    } catch (error) {
      setState(prev => ({
        ...prev,
        error: `处理文件时发生错误: ${error instanceof Error ? error.message : '未知错误'}`
      }));
    } finally {
      setState(prev => ({ ...prev, uploading: false }));
    }
  }, [onFilesAdded]);

  // 拖拽事件处理
  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setState(prev => ({ ...prev, dragActive: true }));
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setState(prev => ({ ...prev, dragActive: false }));
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setState(prev => ({ ...prev, dragActive: false }));

    if (disabled) return;

    const files = FileUtils.extractFilesFromDragEvent(e);
    if (files.length > 0) {
      handleFilesAdded(files);
    }
  }, [disabled, handleFilesAdded]);

  // 文件选择处理
  const handleFileSelect = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = FileUtils.extractFilesFromInput(e.target);
    if (files.length > 0) {
      handleFilesAdded(files);
    }
    // 清空input值以便重复选择相同文件
    e.target.value = '';
  }, [handleFilesAdded]);

  // 粘贴事件处理
  const handlePaste = useCallback((e: ClipboardEvent) => {
    if (disabled || !e.clipboardData) return;

    const items = Array.from(e.clipboardData.items);
    const imageFiles: File[] = [];

    items.forEach(item => {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          imageFiles.push(file);
        }
      }
    });

    if (imageFiles.length > 0) {
      handleFilesAdded(imageFiles);
    }
  }, [disabled, handleFilesAdded]);

  // 注册粘贴事件
  React.useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  // 格式化文件类型显示
  const getAcceptedFormatsText = () => {
    const formats = acceptedFormats
      .map(format => format.split('/')[1].toUpperCase())
      .join(', ');
    return formats;
  };

  // 关闭错误提示
  const handleCloseError = () => {
    setState(prev => ({ ...prev, error: null }));
  };

  return (
    <div className="file-upload-container">
      {/* 错误提示 */}
      {state.error && (
        <Alert
          message="上传出错"
          description={state.error}
          type="error"
          showIcon
          closable
          onClose={handleCloseError}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* 主上传区域 */}
      <div
        className={`file-upload-area ${
          state.dragActive ? 'drag-active' : ''
        } ${disabled ? 'disabled' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="upload-content">
          {/* 图标 */}
          <div className="upload-icon">
            <PictureOutlined style={{ fontSize: 48, color: '#1890ff' }} />
          </div>

          {/* 主标题 */}
          <Title level={4} style={{ margin: '16px 0 8px 0' }}>
            {state.dragActive ? '释放文件开始上传' : '拖拽图片到此处'}
          </Title>

          {/* 副标题 */}
          <Text type="secondary" style={{ fontSize: '14px' }}>
            或点击下方按钮选择文件
          </Text>

          {/* 按钮区域 */}
          <div className="upload-buttons">
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={handleFileSelect}
              loading={state.uploading}
              disabled={disabled}
              size="large"
            >
              选择文件
            </Button>

            <Button
              type="default"
              onClick={() => {
                // 触发系统粘贴
                document.execCommand('paste');
              }}
              disabled={disabled || state.uploading}
              style={{ marginLeft: 12 }}
            >
              粘贴图片
            </Button>
          </div>

          {/* 文件信息 */}
          <div className="upload-info">
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <div>支持格式：{getAcceptedFormatsText()}</div>
              <div>单文件最大：{FileUtils.formatFileSize(maxFileSize)}</div>
              <div>批量最多：{maxFiles} 个文件</div>
              <div>支持粘贴剪贴板图片</div>
            </Text>
          </div>
        </div>
      </div>

      {/* 隐藏的文件输入 */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFormats.join(',')}
        multiple
        onChange={handleInputChange}
        style={{ display: 'none' }}
      />

      {/* 功能说明 */}
      <div className="upload-features">
        <div className="feature-item">
          <InboxOutlined style={{ color: '#52c41a' }} />
          <span>支持拖拽上传</span>
        </div>
        <div className="feature-item">
          <UploadOutlined style={{ color: '#1890ff' }} />
          <span>支持批量选择</span>
        </div>
        <div className="feature-item">
          <PictureOutlined style={{ color: '#faad14' }} />
          <span>自动图片预览</span>
        </div>
        <div className="feature-item">
          <ExclamationCircleOutlined style={{ color: '#f5222d' }} />
          <span>智能格式检测</span>
        </div>
      </div>
    </div>
  );
};